import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useSession } from "./lib/auth";
import { OPSBELT_APP_URL } from "./lib/env";
import { Layout } from "./routes/Layout";
import { LoginPage } from "./routes/login";
import { OverviewPage } from "./routes/overview";
import { ConnectorsPage } from "./routes/connectors";
import { ConnectorDetailPage } from "./routes/connector-detail";
import { ConnectWizardPage } from "./routes/connect";
import { DestinationsPage } from "./routes/destinations";
import { DestinationDetailPage } from "./routes/destination-detail";
import { KeysPage } from "./routes/keys";
import { AuditPage } from "./routes/audit";

// Billing lives in the OpsBelt admin dashboard, not BlobShip — bounce there.
function BillingRedirect() {
  useEffect(() => {
    window.location.href = `${OPSBELT_APP_URL}/billing`;
  }, []);
  return (
    <div className="px-1 py-16 text-center text-sm text-ink-muted">
      Taking you to OpsBelt billing…
    </div>
  );
}

function FullScreenSpinner() {
  return (
    <div className="flex h-full items-center justify-center">
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-surface-300 border-t-accent" />
    </div>
  );
}

// Redirects to /login if there is no session, preserving the intended path.
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  const location = useLocation();

  if (isPending) return <FullScreenSpinner />;
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route path="/" element={<OverviewPage />} />
        <Route path="/connectors" element={<ConnectorsPage />} />
        <Route path="/connectors/:id" element={<ConnectorDetailPage />} />
        <Route path="/connect" element={<ConnectWizardPage />} />
        <Route path="/destinations" element={<DestinationsPage />} />
        <Route path="/destinations/:id" element={<DestinationDetailPage />} />
        <Route path="/keys" element={<KeysPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/billing" element={<BillingRedirect />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
